import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from '@/auth/AuthContext';
import client from '@/api/client';

export const THEMES = ['light', 'slate-dark', 'ocean-blue', 'forest-green', 'midnight-violet', 'lavender', 'blue', 'soft-grey'] as const;
export type ThemeId = (typeof THEMES)[number];

const DEFAULT_THEME: ThemeId = 'light';

function isThemeId(value: string | undefined): value is ThemeId {
  return !!value && (THEMES as readonly string[]).includes(value);
}

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, updateUser } = useAuth();
  const [theme, setThemeState] = useState<ThemeId>(
    isThemeId(user?.themePreference) ? (user!.themePreference as ThemeId) : DEFAULT_THEME,
  );

  useEffect(() => {
    if (isThemeId(user?.themePreference)) {
      setThemeState(user!.themePreference as ThemeId);
    }
  }, [user?.themePreference]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const setTheme = useCallback((next: ThemeId) => {
    setThemeState(next);
    updateUser({ themePreference: next });
    client.patch('/api/auth/me/theme', { theme: next }).catch(() => {
      // Low-stakes preference update — keep the optimistic UI even if the sync fails.
    });
  }, [updateUser]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
